// steps/MessageContent.jsx
import React, { useEffect, useState } from 'react';
import SearchableSelect from '../SearchableSelect';
import {replaceSmsLink,smsTrackingMismatch} from '../smsTrackingLink';

const MAX_CHARS = 160;
const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;

const MessageContent = ({ formData, updateFormData, setIsFormValid }) => {
  const [templates, setTemplates] = useState([]);
  const [pickedId, setPickedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedName, setSavedName] = useState('');
  const [newName, setNewName] = useState('');
  const [naming, setNaming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  const charCount = formData.messageText.length;

  useEffect(() => {
    setIsFormValid(!!formData.messageText.trim() && !smsTrackingMismatch(formData));
  }, [formData.messageText, formData.shortUrl, formData.rebrandlyId, setIsFormValid]);

  useEffect(() => {
    fetch('/api/v1/sms_templates', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const picked = templates.find((t) => String(t.id) === String(pickedId)) || null;

  // Ссылка шаблона вписана прямо в его текст. Кампании нужна своя короткая ссылка на тот же
  // destination (иначе клики разных рассылок сольются), поэтому создаём её здесь же и
  // подменяем ссылку шаблона в тексте — в SMS должна уйти та ссылка, по которой считаем клики.
  const campaignLinkFor = async (template) => {
    if (!template.short_url || !template.destination_url) return null;

    if (formData.rebrandlyId && formData.destinationUrl === template.destination_url) {
      return {
        shortUrl: formData.shortUrl,
        rebrandlyId: formData.rebrandlyId,
        slashtag: formData.slashtag,
        linkCreatedAt: formData.linkCreatedAt
      };
    }

    const res = await fetch('/api/v1/rebrandly/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({
        destination_url: template.destination_url,
        title: formData.name?.trim() || template.name
      })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create link');

    // Прежняя ссылка кампании никуда не ушла — кампания ещё не создана.
    if (formData.rebrandlyId) {
      fetch(`/api/v1/rebrandly/${formData.rebrandlyId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() }
      }).catch(() => {});
    }

    return {
      shortUrl: data.shortUrl,
      rebrandlyId: data.id,
      slashtag: data.slashtag,
      linkCreatedAt: data.createdAt
    };
  };

  const applyTemplate = async () => {
    if (!picked) return;

    setApplying(true);
    setApplyError('');
    setSavedName('');

    let link = null;
    try {
      link = await campaignLinkFor(picked);
    } catch (e) {
      setApplyError(
        `Could not create a short link for this campaign (${e.message}). ` +
          'The text and destination were copied — create the link in Campaign settings.'
      );
    }

    let text = picked.text;
    if (link) {
      text = replaceSmsLink(text,picked.short_url,link.shortUrl);
    } else if (picked.short_url && text.includes(picked.short_url)) {
      setApplyError((prev) =>
        prev || 'The text contains the template’s short link — replace it with this campaign’s link.'
      );
    }

    updateFormData({
      messageText: text,
      destinationUrl: picked.destination_url || '',
      smsTemplateId: picked.id,
      smsTemplateName: picked.name,
      ...(link && { ...link, fullShortUrl: `https://${link.shortUrl}` })
    });
    setApplying(false);
  };

  const saveAsTemplate = async () => {
    const name = newName.trim();
    if (!name) return;

    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch('/sms_templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({
          sms_template: {
            name: name.trim(),
            text: formData.messageText,
            destination_url: formData.destinationUrl || null,
            short_url: formData.shortUrl || null,
            rebrandly_id: formData.rebrandlyId || null,
            slashtag: formData.slashtag || null
          }
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data.errors || []).join('. ') || 'Save failed');

      updateFormData({ smsTemplateId: data.id, smsTemplateName: data.name });
      setTemplates((prev) => [data, ...prev]);
      setSavedName(data.name);
      setNaming(false);
      setNewName('');
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMessageChange = (value) => {
    if (value.length <= MAX_CHARS) updateFormData({ messageText: value });
  };

  return (
    <div className='space-y-6'>
      <div>
        <label className='block text-gray-700 text-sm font-bold mb-2'>
          Use a saved template <span className='font-normal text-gray-400'>(optional)</span>
        </label>
        <div className='flex gap-2 items-start'>
          <div className='flex-1 min-w-0'>
            <SearchableSelect
              options={templates.map((t) => ({ value: t.id, label: t.name }))}
              value={pickedId}
              onChange={(v) => setPickedId(v || '')}
              placeholder={templates.length ? 'Pick a template…' : 'No templates yet'}
              isClearable
            />
          </div>
          <button
            type='button'
            onClick={applyTemplate}
            disabled={!picked || applying}
            className={`shrink-0 px-3 py-2 text-sm rounded text-white ${
              picked && !applying ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {applying ? 'Applying…' : 'Use this text'}
          </button>
        </div>
        {applyError && <p className='mt-2 text-xs text-red-600'>{applyError}</p>}

        {picked && (
          <div className='mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50'>
            <p className='text-xs text-gray-500 mb-1'>Template preview</p>
            <div className='text-sm text-gray-900 whitespace-pre-wrap break-words'>{picked.text}</div>
            {picked.destination_url && (
              <p className='mt-2 text-xs text-gray-500'>Destination: {picked.destination_url}</p>
            )}
            {picked.full_short_url && (
              <p className='mt-1 text-xs text-gray-500'>Short link: {picked.full_short_url}</p>
            )}
            <p className='mt-2 text-xs text-gray-400'>
              Applying copies the text and destination into the campaign. Editing them here does not
              change the template.
              {picked.short_url &&
                ' The campaign gets its own short link to the same destination, so clicks are counted per campaign.'}
            </p>
          </div>
        )}
      </div>

      <div>
        <div className='flex justify-between items-center mb-2'>
          <label className='block text-gray-700 text-sm font-bold' htmlFor='messageText'>
            Message Text *
          </label>
          <span className={`text-sm ${charCount > MAX_CHARS - 20 ? 'text-red-500' : 'text-gray-500'}`}>
            {charCount}/{MAX_CHARS} characters
          </span>
        </div>
        <textarea
          id='messageText'
          value={formData.messageText}
          onChange={(e) => handleMessageChange(e.target.value)}
          className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
          rows='4'
          placeholder='Enter your message text here...'
        />
        {smsTrackingMismatch(formData) && <p role='alert' className='sms-tracking-notice'>The selected tracking link is missing from the SMS. Insert {formData.shortUrl} in the message before launching; otherwise clicks would be counted for a different link.</p>}

        <div className='mt-2 flex items-center gap-3 flex-wrap'>
          {naming ? (
            <div className='flex items-center gap-2'>
              <input
                autoFocus
                className='shadow-sm border border-gray-300 rounded px-2 py-1 text-sm'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder='Template name'
              />
              <button
                type='button'
                onClick={saveAsTemplate}
                disabled={!newName.trim() || saving}
                className={`text-sm px-2 py-1 rounded text-white ${
                  newName.trim() && !saving ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type='button'
                onClick={() => { setNaming(false); setSaveError(''); }}
                className='text-sm text-gray-500 hover:text-gray-700'
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type='button'
              onClick={() => { setNaming(true); setNewName(formData.name || ''); }}
              disabled={!formData.messageText.trim()}
              className={`text-sm ${
                formData.messageText.trim()
                  ? 'text-blue-600 hover:text-blue-800'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              Save as new template
            </button>
          )}
          {savedName && (
            <span className='text-xs text-green-700'>
              Saved as “{savedName}” — this campaign now uses it.
            </span>
          )}
          {saveError && <span className='text-xs text-red-600'>{saveError}</span>}
        </div>
      </div>

      <div className='mt-8'>
        <h4 className='text-gray-700 font-bold mb-3'>Message Preview</h4>
        <div className='bg-gray-100 p-4 rounded-lg'>
          <div className='bg-white rounded-lg p-4 shadow-sm max-w-sm mx-auto'>
            <div className='text-gray-900 whitespace-pre-wrap break-words'>
              {formData.messageText || 'Your message will appear here...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageContent;
